/** Inclusive cell bounds of a token within the base grid. */
type GridAreaBounds = {
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
};

export type ParsedGridAreas = {
  /** Distinct tokens in first-appearance (row-major) order — the widget fill order. */
  tokensInOrder: string[];
  boundsByToken: Map<string, GridAreaBounds>;
  rowCount: number;
  columnCount: number;
};

/**
 * Parses a `grid-template-areas`-style map into per-token cell bounds. Rows are separated by
 * a newline or a pipe (`|`) — so a map can be a multi-line template literal or a one-liner
 * like `'A A B | C D E'` — and cells within a row by whitespace. Every row must have the same
 * number of cells, and each token must form a filled rectangle (no L-shapes / gaps) — otherwise
 * it throws.
 */
export const parseGridAreas = (areas: string): ParsedGridAreas => {
  const grid = areas
    .split(/[\n|]/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .map(line => line.split(/\s+/));

  if (grid.length === 0) {
    throw new Error('Grid area map is empty');
  }

  const columnCount = grid[0].length;
  grid.forEach((cells, rowIndex) => {
    if (cells.length !== columnCount) {
      throw new Error(
        `Grid area map row ${rowIndex} has ${cells.length} cells, expected ${columnCount}`
      );
    }
  });

  const tokensInOrder: string[] = [];
  const boundsByToken = new Map<string, GridAreaBounds>();

  grid.forEach((cells, rowIndex) => {
    cells.forEach((token, colIndex) => {
      const bounds = boundsByToken.get(token);
      if (!bounds) {
        tokensInOrder.push(token);
        boundsByToken.set(token, {
          minRow: rowIndex,
          maxRow: rowIndex,
          minCol: colIndex,
          maxCol: colIndex,
        });
        return;
      }
      bounds.minRow = Math.min(bounds.minRow, rowIndex);
      bounds.maxRow = Math.max(bounds.maxRow, rowIndex);
      bounds.minCol = Math.min(bounds.minCol, colIndex);
      bounds.maxCol = Math.max(bounds.maxCol, colIndex);
    });
  });

  boundsByToken.forEach((bounds, token) => {
    for (let row = bounds.minRow; row <= bounds.maxRow; row++) {
      for (let col = bounds.minCol; col <= bounds.maxCol; col++) {
        if (grid[row][col] !== token) {
          throw new Error(`Grid area token "${token}" is not a rectangle`);
        }
      }
    }
  });

  return { tokensInOrder, boundsByToken, rowCount: grid.length, columnCount };
};
